import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { GamePage } from './GamePage';
import { StockCategoryFilter, CategoryHelpers } from '../types/stockCategories';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useCategoryGame } from '../contexts/GameContext';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { AlertCircle, Home, Loader2, RefreshCw } from 'lucide-react';
import { useCategoryBootstrap } from '../hooks/useCategoryBootstrap';
import { usePageSEO } from '../hooks/usePageSEO';
import { buildCategorySeoConfig, isSeoCategory } from '../seo/categorySeoConfig';
import { useCategoryLocking } from '../hooks/useCategoryLocking/index';
import { useEntitlement } from '../hooks/useEntitlement';
import { useAccessControl } from '../contexts/AccessControlContext';
import { getCurrentGameTimestamp } from '../config/dateTimeConfig';
import { useGameStart } from '../contexts/GameStartContext';
import { GameStartOverlay } from '../components/game/GameStartOverlay';
import { cn } from '../utils/cn';

interface CategoryGamePageProps {
  className?: string;
  routeCategory?: StockCategoryFilter;
}

/**
 * Category-specific game page that wraps the main GamePage with category context
 */
export const CategoryGamePage: React.FC<CategoryGamePageProps> = ({
  className,
  routeCategory
}) => {
  const params = useParams<{ category?: string }>();
  const paramCategory = params.category;
  const resolvedCategory = paramCategory ?? routeCategory;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentCategory, switchCategory } = useCategoryGame();
  const { isCategoryLocked, handleLockedCategoryClick, CategoryLockModal } = useCategoryLocking();
  const { is_premium, loading: entitlementLoading } = useEntitlement();
  const { premiumMode } = useAccessControl();
  const { hasGameStarted, startGame, setCategory: setGameStartCategory } = useGameStart();

  // Replay mode: derive gameDate from ?date= query param
  const dateParam = searchParams.get('date');
  const todayUTC = getCurrentGameTimestamp();
  const gameDate = dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam) ? dateParam : undefined;
  const isReplayMode = Boolean(gameDate && gameDate !== todayUTC);

  // Premium gate: strip date param for non-premium users (skip when PREMIUM_MODE=FREE)
  useEffect(() => {
    if (isReplayMode && !is_premium && premiumMode !== 'FREE' && !entitlementLoading) {
      setSearchParams({}, { replace: true });
    }
  }, [isReplayMode, is_premium, premiumMode, entitlementLoading, setSearchParams]);

  // Removed console.log that executed on every render

  // Router initialization timeout state
  const [routerTimeout, setRouterTimeout] = useState(false);

  // Timeout for router initialization - fallback after 3 seconds
  useEffect(() => {
    if (resolvedCategory === undefined) {
      if (process.env.NODE_ENV === 'development') {
        console.log('⏰ [CategoryGamePage] Starting router initialization timeout (3s)');
      }
      const timer = setTimeout(() => {
        if (process.env.NODE_ENV === 'development') {
          console.log('⏰ [CategoryGamePage] Router initialization timeout reached - forcing fallback to spy');
          console.log('🧭 [CategoryGamePage] Current window.location before timeout:', window.location.href);
        }

        // Try to navigate manually if router isn't working
        if (window.location.pathname === '/') {
          if (process.env.NODE_ENV === 'development') {
            console.log('🧭 [CategoryGamePage] Attempting manual navigation to /popular');
          }
          window.history.pushState({}, '', '/popular');
          // Force a page refresh to trigger router
          window.location.reload();
        }

        setRouterTimeout(true);
      }, 3000);

      return () => {
        if (process.env.NODE_ENV === 'development') {
          console.log('⏰ [CategoryGamePage] Clearing router timeout');
        }
        clearTimeout(timer);
      };
    } else {
      setRouterTimeout(false);
    }
  }, [resolvedCategory]);

  // Handle router initialization - category is undefined during initial load
  const isRouterInitializing = resolvedCategory === undefined && !routerTimeout;

  // Handle router timeout fallback
  const effectiveCategory = routerTimeout ? 'popular' : resolvedCategory;

  // Validate category parameter only after router has initialized
  const isValidCategory = !isRouterInitializing && typeof effectiveCategory === 'string' && CategoryHelpers.isValidCategory(effectiveCategory);
  const categoryFilter = isValidCategory ? effectiveCategory as StockCategoryFilter : (routerTimeout ? 'popular' as StockCategoryFilter : null);

  // Removed console.log that executed on every render

  // Get category display config
  const categoryConfig = categoryFilter ? CategoryHelpers.getConfig(categoryFilter) : null;
  const categoryLabel = categoryConfig?.label;

  const isLocked = categoryFilter ? isCategoryLocked(categoryFilter) : false;

  const bootstrapDisabled = isRouterInitializing || !categoryFilter || !categoryConfig || isLocked;
  const bootstrapCategory = (categoryFilter ?? 'popular') as StockCategoryFilter;
  const bootstrap = useCategoryBootstrap({
    category: bootstrapCategory,
    disabled: bootstrapDisabled,
    date: gameDate
  });
  const { bootstrapState, refetchAll } = bootstrap;
  const showHintsNotice = Boolean(
    !bootstrapDisabled &&
    bootstrapState.isPageReady &&
    bootstrapState.isAwaitingHints
  );

  // Stable callback functions
  const handleRetry = useCallback(() => {
    refetchAll().catch((error) => console.error('Retry failed', error));
  }, [refetchAll]);

  const handleNavigateToSpy = useCallback(() => {
    navigate('/popular');
  }, [navigate]);

  const handleNavigateToSpyReplace = useCallback(() => {
    navigate('/popular', { replace: true });
  }, [navigate]);

  const handleOverlayPlay = useCallback(() => {
    if (categoryFilter && isCategoryLocked(categoryFilter)) {
      handleLockedCategoryClick(categoryFilter, {
        featureName: categoryLabel,
        message: categoryLabel
          ? `Upgrade to a Wallstreetle Membership to play the ${categoryLabel} category.`
          : undefined
      });
    } else {
      startGame();
    }
  }, [categoryFilter, isCategoryLocked, handleLockedCategoryClick, categoryLabel, startGame]);

  // Switch category in context when route changes
  useEffect(() => {
    if (categoryFilter && categoryFilter !== currentCategory) {
      switchCategory(categoryFilter);
    }
  }, [categoryFilter, currentCategory, switchCategory]);

  // Sync category with game start context to determine if overlay should show
  useEffect(() => {
    if (categoryFilter) {
      setGameStartCategory(categoryFilter, isReplayMode);
    }
  }, [categoryFilter, isReplayMode, setGameStartCategory]);

  // Emergency fallback - if we're still at root after initial render, try to navigate
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!resolvedCategory && !routerTimeout && window.location.pathname === '/') {
        try {
          handleNavigateToSpyReplace();
        } catch (error) {
          console.error('Navigate failed:', error);
        }
      }
    }, 1000); // Try after 1 second

    return () => clearTimeout(timer);
  }, [resolvedCategory, routerTimeout, handleNavigateToSpyReplace]);

  // Enhance SEO metadata when category is resolved
  const seoInfo = React.useMemo(() => {
    if (!categoryConfig || !categoryFilter || !isSeoCategory(categoryFilter)) {
      return null;
    }

    const origin = typeof window !== 'undefined' && window.location.origin
      ? window.location.origin
      : 'https://wallstreetle.com';

    // Special SEO for root page (popular category at /)
    const isRootPage = typeof window !== 'undefined' && window.location.pathname === '/' && categoryFilter === 'popular';

    if (isRootPage) {
      // Use StartPage SEO for the root page
      const canonicalUrl = `${origin}/`;
      return {
        title: 'Wallstreetle | The Stock Wordle',
        description: 'Play Wallstreetle, the Stock Wordle. Solve the daily ticker puzzle with real charts, financial metrics, and guided hints across SPY, Tech, NASDAQ, WallStreetBets, and more.',
        keywords: ['Stock Wordle', 'stock wordle', 'finance wordle', 'stock guessing game', 'guess the stock game', 'ticker guessing', 'equity game', 'stock trivia', 'investing puzzle', 'daily stock challenge', 'daily stock puzzle', 'stock market game', 'play stock game', 'free stock game'],
        heroHeading: 'Wallstreetle | The Stock Wordle',
        heroDescription: 'Wallstreetle is the Stock Wordle: play a daily ticker puzzle with real market data, charts, and hints across every major category.',
        canonicalPath: '/',
        canonicalUrl,
        ogImage: `${origin}/assets/share-link-image.png`,
        structuredData: {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Wallstreetle - Stock Guessing Game Categories',
          description: 'Play the ultimate stock wordle and finance wordle! This guess the stock game features multiple categories with ticker guessing, stock trivia, and investing puzzles. Free daily stock challenge with live charts and hints.',
          url: canonicalUrl,
          inLanguage: 'en-US',
          author: {
            '@type': 'Person',
            name: 'Wallstreetle Team',
            url: 'https://wallstreetle.com'
          },
          publisher: {
            '@type': 'Organization',
            name: 'Wallstreetle',
            logo: {
              '@type': 'ImageObject',
              url: 'https://wallstreetle.com/logo512.png',
              width: 512,
              height: 512
            }
          },
          datePublished: '2024-09-01',
          dateModified: new Date().toISOString().split('T')[0],
          mainEntity: {
            '@type': 'Game',
            name: 'Wallstreetle Stock Guessing Game',
            description: 'Stock wordle and finance wordle - a daily stock challenge where you guess the stock game using charts, ticker guessing, and financial metrics. Free equity game and investing puzzle.',
            author: {
              '@type': 'Person',
              name: 'Wallstreetle Team',
              url: 'https://wallstreetle.com'
            },
            publisher: {
              '@type': 'Organization',
              name: 'Wallstreetle',
              logo: {
                '@type': 'ImageObject',
                url: 'https://wallstreetle.com/logo512.png',
                width: 512,
                height: 512
              }
            },
            genre: ['Puzzle', 'Educational', 'Finance'],
            gamePlatform: 'Web Browser',
            numberOfPlayers: 1,
            inLanguage: 'en-US'
          }
        }
      };
    }

    // Standard category SEO
    const resolved = buildCategorySeoConfig(categoryFilter, categoryConfig);
    if (!resolved) {
      return null;
    }
    const canonicalUrl = `${origin}${resolved.canonicalPath}`;

    return {
      ...resolved,
      canonicalUrl,
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: resolved.title,
        description: resolved.description,
        url: canonicalUrl,
        inLanguage: 'en-US',
        author: {
          '@type': 'Person',
          name: 'Wallstreetle Team',
          url: 'https://wallstreetle.com'
        },
        publisher: {
          '@type': 'Organization',
          name: 'Wallstreetle',
          logo: {
            '@type': 'ImageObject',
            url: 'https://wallstreetle.com/logo512.png',
            width: 512,
            height: 512
          }
        },
        datePublished: '2024-09-01',
        dateModified: new Date().toISOString().split('T')[0],
        isPartOf: {
          '@type': 'WebSite',
          name: 'Wallstreetle',
          url: 'https://wallstreetle.com',
        },
        about: {
          '@type': 'Thing',
          name: categoryConfig.label,
          description: categoryConfig.description,
        },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Wallstreetle',
              item: 'https://wallstreetle.com/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: categoryConfig.label,
              item: canonicalUrl,
            },
          ],
        },
      },
    };
  }, [categoryConfig, categoryFilter]);

  usePageSEO(seoInfo ? {
    title: seoInfo.title,
    description: seoInfo.description,
    canonicalUrl: seoInfo.canonicalUrl,
    keywords: seoInfo.keywords,
    ogImage: seoInfo.ogImage,
    twitterImage: seoInfo.ogImage,
    structuredData: seoInfo.structuredData,
  } : null);

  // Maintain document title for scenarios where meta tags might be stripped (e.g., preview embeds)
  useDocumentTitle(
    seoInfo?.title ??
    (categoryConfig
      ? `Wallstreetle | ${categoryConfig.label} Stock Game`
      : 'Wallstreetle | Daily Stock Market Game')
  );

  // Show loading state while router is initializing OR when component first mounts
  // Since we're using key-based remounting, every category change will trigger this
  if (isRouterInitializing) {
    return (
      <>
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <h2 className="text-lg font-semibold mb-2">Loading game...</h2>
            <p className="text-gray-600 dark:text-gray-400">
              {categoryConfig ? `Preparing your ${categoryConfig.label} stock challenge` : 'Preparing your stock challenge'}
            </p>
          </div>
        </div>
        {CategoryLockModal}
      </>
    );
  }

  // Handle truly invalid category (after router has initialized)
  if (!isValidCategory || !categoryFilter || !categoryConfig) {
    return (
      <>
        <div className="min-h-screen flex items-center justify-center p-4">
          <Card className="max-w-md w-full p-6 text-center">
            <AlertCircle className="mx-auto mb-4 text-red-500" size={48} />
            <h2 className="text-lg font-semibold mb-2">Invalid Category</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              The category "{resolvedCategory ?? paramCategory ?? routeCategory}" is not recognized. Please select a valid category.
            </p>
            <Button
              onClick={handleNavigateToSpy}
              className="w-full"
            >
              <Home className="w-4 h-4 mr-2" />
              Go to SPY Game
            </Button>
          </Card>
        </div>
        {CategoryLockModal}
      </>
    );
  }

  // Show error card if bootstrap failed
  if (!bootstrapDisabled && bootstrapState.error) {
    return (
      <>
        <div className="min-h-screen flex items-center justify-center p-4">
          <Card className="max-w-lg w-full p-6 text-center space-y-4">
            <AlertCircle className="mx-auto text-red-500" size={48} />
            <div>
              <h2 className="text-lg font-semibold mb-2">We couldn't load today's data</h2>
              <p className="text-gray-600 dark:text-gray-400">
                {bootstrapState.error}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={handleRetry}
                className="flex-1"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry loading
              </Button>
              <Button
                variant="outline"
                onClick={handleNavigateToSpy}
                className="flex-1"
              >
                <Home className="w-4 h-4 mr-2" />
                Go to SPY game
              </Button>
            </div>
          </Card>
        </div>
        {CategoryLockModal}
      </>
    );
  }

  // Show start overlay if game hasn't started — even while data is still loading
  // Locked categories always show the overlay (Play opens upgrade prompt instead)
  if ((!hasGameStarted || isLocked) && categoryFilter && categoryConfig) {
    return (
      <>
        <div className={className} data-category={categoryFilter}>
          <GameStartOverlay
            category={categoryFilter}
            categoryConfig={categoryConfig}
            onPlay={handleOverlayPlay}
            isLoading={entitlementLoading || (!isLocked && !bootstrapState.isPageReady)}
          />
        </div>
        {CategoryLockModal}
      </>
    );
  }

  // Render the game page with category context
  return (
    <>
      <div className={cn(className, 'animate-fade-up opacity-0')} data-category={categoryFilter}>
        {showHintsNotice && (
          <div className="w-full bg-muted/40 dark:bg-muted/30 border-b border-border">
            <div className="max-w-7xl mx-auto px-4 py-2 flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Fetching fresh hints in the background…</span>
            </div>
          </div>
        )}
        <GamePage
          category={categoryFilter}
          categoryConfig={categoryConfig}
          bootstrap={bootstrap}
          isReplayMode={isReplayMode}
          gameDate={gameDate}
        />
      </div>
      {CategoryLockModal}
    </>
  );
};

export default CategoryGamePage;
